import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import * as api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const OfflineSyncContext = createContext();

export function useOfflineSync() {
  return useContext(OfflineSyncContext);
}

const QUEUE_KEY = "colygon_offline_queue";

export function OfflineSyncProvider({ children }) {
  const { t } = useI18n();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queue, setQueue] = useState([]);

  // Cargar cola inicial
  useEffect(() => {
    try {
      const stored = localStorage.getItem(QUEUE_KEY);
      if (stored) {
        setQueue(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Error reading offline queue", e);
    }
  }, []);

  // Sincronizar cola con localStorage
  useEffect(() => {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }, [queue]);

  // message: aviso propio de quien encola (si no, el genérico)
  const enqueueAction = useCallback((actionType, payload, message) => {
    const action = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 9),
      type: actionType,
      payload,
      timestamp: Date.now(),
    };
    setQueue(prev => {
      // Prevent duplicate actions of the same type+payload already in queue.
      const key = JSON.stringify({ type: actionType, payload });
      if (prev.some(a => JSON.stringify({ type: a.type, payload: a.payload }) === key)) return prev;
      return [...prev, action];
    });
    toast.info(message || t("offline.sync_pending") || "Sin conexión. La acción se sincronizará cuando recuperes la red.",
               { duration: message ? 6000 : 4000 });
  }, [t]);

  // Una sola sincronización a la vez
  const syncingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (queue.length === 0 || syncingRef.current) return;
    
    // Si todavía dice offline, no intentamos
    if (!navigator.onLine) return;
    syncingRef.current = true;

    toast.loading(t("offline.syncing") || "Sincronizando acciones pendientes...", { id: "offline-sync" });
    
    let currentQueue = [...queue];
    const failedActions = [];
    const rejected = [];
    
    for (const action of currentQueue) {
      try {
        if (action.type === "checkinRoute") {
          await api.checkinRoute(...action.payload);
        } else if (action.type === "completeRoute") {
          await api.completeRoute(...action.payload);
        } else if (action.type === "createRoute") {
          await api.createRoute(...action.payload);
        } else if (action.type === "startRoute") {
          // Antes se encolaba desde "Mi viaje" pero aquí no se procesaba
          await api.startRoute(...action.payload);
        } else if (action.type === "markPassenger") {
          await api.markPassenger(...action.payload);
        }
        // Acción exitosa, se ignora del failedActions
      } catch (err) {
        // Si es error de red, la devolvemos a la cola. 
        // Si es 400 (ej. ya estaba escaneado), la descartamos para no bloquear la cola.
        if (!err.response) {
          failedActions.push(action);
        } else if (err.response.status !== 409 && !/ya has hecho check-in/i.test(err.response.data?.detail || "")) {
          rejected.push(err.response.data?.detail || "");
        }
      }
    }

    setQueue(failedActions);
    syncingRef.current = false;

    // Lo que el servidor rechazó (p. ej. una validación lejos del centro) no
    // se da por hecho: antes salía «¡Sincronización completada!»
    if (rejected.length > 0) {
      toast.error((t("offline.sync_rejected") || "No se pudo guardar una acción pendiente: {detail}")
        .replace("{detail}", rejected[0]), { id: "offline-sync", duration: 10000 });
    } else if (failedActions.length === 0) {
      toast.success(t("offline.sync_complete") || "¡Sincronización completada!", { id: "offline-sync" });
      // Forzar recarga sutil o emitir evento para que el dashboard actualice
      window.dispatchEvent(new CustomEvent("colygon-sync-complete"));
    } else {
      toast.error(
        (t("offline.sync_partial") || "Sincronización parcial. Quedan {count} pendientes.").replace("{count}", failedActions.length), 
        { id: "offline-sync" }
      );
    }
  }, [queue, t]);

  const manualSync = useCallback(async () => {
    toast.loading(t("offline.checking") || "Comprobando conexión...", { id: "manual-sync" });
    try {
      // Usamos la función ya exportada fetchMe para hacer ping ligero
      await api.fetchMe(); 
      setIsOffline(false);
      toast.success(t("offline.connected") || "Conexión confirmada.", { id: "manual-sync" });
      if (queue.length > 0) {
        processQueue();
      }
    } catch (err) {
      setIsOffline(true);
      toast.error(t("offline.still_offline") || "Sin conexión con el servidor. Revisa tu cobertura.", { id: "manual-sync" });
    }
  }, [processQueue, queue.length, t]);

  // Los avisos de red y el temporizador llaman siempre a la última versión.
  // Antes el efecto dependía de la cola: cada intento fallido la cambiaba y
  // volvía a intentarlo al instante, en bucle, con los avisos parpadeando,
  // si el móvil decía tener red pero el servidor no respondía.
  const processRef = useRef(processQueue);
  processRef.current = processQueue;

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      processRef.current();
    };

    const handleOffline = () => {
      setIsOffline(true);
      toast.warning(t("offline.went_offline") || "Has perdido la conexión. Entrando en modo offline.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Al arrancar (por si nos perdimos el evento) y cada 30 s mientras quede algo
    const first = setTimeout(() => processRef.current(), 1500);
    const every = setInterval(() => processRef.current(), 30000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearTimeout(first);
      clearInterval(every);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OfflineSyncContext.Provider value={{ isOffline, queue, enqueueAction, manualSync }}>
      {children}
    </OfflineSyncContext.Provider>
  );
}
